import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import './AdminUserListPage.css';

interface AdminUser {
  id: number;
  email: string;
  username: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

const AdminUserListPage: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    name: '',
    password: ''
  });
  const [formError, setFormError] = useState<string | null>(null);

  const fetchAdmins = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const data = await adminApi.getAllAdmins(token);
      setAdmins(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch admin users');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!token) return;

    try {
      await adminApi.createAdmin(formData, token);
      setShowModal(false);
      setFormData({ email: '', username: '', name: '', password: '' });
      fetchAdmins();
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to create admin user');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormData({ email: '', username: '', name: '', password: '' });
    setFormError(null);
  };

  if (loading) {
    return <div className="admin-users-page"><p>{t('admin.adminUsers.loading')}</p></div>;
  }

  if (error) {
    return <div className="admin-users-page"><p className="error">{error}</p></div>;
  }

  return (
    <div className="admin-users-page">
      <div className="page-header">
        <h1>{t('admin.adminUsers.title')}</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          {t('admin.adminUsers.addAdmin')}
        </button>
      </div>

      <div className="admin-users-table-container">
        <table className="admin-users-table">
          <thead>
            <tr>
              <th>{t('admin.adminUsers.id')}</th>
              <th>{t('admin.adminUsers.email')}</th>
              <th>{t('admin.adminUsers.username')}</th>
              <th>{t('admin.adminUsers.name')}</th>
              <th>{t('admin.adminUsers.createdAt')}</th>
            </tr>
          </thead>
          <tbody>
            {admins.map((admin) => (
              <tr key={admin.id}>
                <td>{admin.id}</td>
                <td>{admin.email}</td>
                <td>{admin.username}</td>
                <td>{admin.name}</td>
                <td>{new Date(admin.createdAt).toLocaleDateString('hu-HU')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('admin.adminUsers.createAdmin')}</h2>
            <form onSubmit={handleSubmit}>
              {formError && <div className="error-message">{formError}</div>}
              
              <div className="form-group">
                <label htmlFor="email">{t('admin.adminUsers.email')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="username">{t('admin.adminUsers.username')}</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="name">{t('admin.adminUsers.name')}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">{t('admin.adminUsers.password')}</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  {t('admin.adminUsers.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('admin.adminUsers.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserListPage;
